import { Box, Stack, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signInWithPopup, GoogleAuthProvider, signOut } from "firebase/auth";
import { useEffect, useState } from 'react';
import { auth, db } from '../firebaseData';
import { findDocumentByField, version } from '../library'
import { useLocation } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import LanguageSelect from '../components/language/select';
import { useLanguage } from '../contexts/LanguageContext';
import { getDocs, collection, addDoc } from 'firebase/firestore';

const GoogleIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

export default function SignIn() {
    const location = useLocation();
    const [openSnackbar, setOpenSnackbar] = useState(location.state?.showSnackbar || false);
    const [snackbarMessage, setSnackbarMessage] = useState('Your account does not have access!');
    const navigate = useNavigate();
    const { language, setLanguage } = useLanguage();

    const [showRequestForm, setShowRequestForm] = useState(false);
    const [pendingUser, setPendingUser] = useState<any>(null);
    const [requestSent, setRequestSent] = useState(false);

    useEffect(() => {
        onAuthStateChanged(auth, async (user) => {
            if (user) {
                let arrData: any[] = await findDocumentByField('users', 'email', user.email);
                if (arrData.length > 0) {
                    navigate('/home');
                } else {
                    // Check if users collection is empty
                    const usersSnapshot = await getDocs(collection(db, 'users'));
                    if (usersSnapshot.empty) {
                        // First user gets admin
                        await addDoc(collection(db, 'users'), {
                            email: user.email,
                            name: user.displayName || 'Admin',
                            role: 'admin',
                            created: new Date(),
                            updated: new Date()
                        });
                        navigate('/home');
                    } else {
                        setPendingUser(user);
                        setShowRequestForm(true);
                    }
                }
            } else {
                navigate('/');
            }
        });
    }, [navigate]);

    const handleLogin = () => {
        const provider = new GoogleAuthProvider();
        signInWithPopup(auth, provider).catch((error) => {
            console.error(error);
            setSnackbarMessage(language === 'en' ? 'Login failed' : 'Đăng nhập thất bại');
            setOpenSnackbar(true);
        });
    };

    const handleRequestAccess = async () => {
        if (pendingUser) {
            try {
                await addDoc(collection(db, 'join_requests'), {
                    email: pendingUser.email,
                    name: pendingUser.displayName || 'Unknown',
                    status: 'pending',
                    created: new Date()
                });
                setRequestSent(true);
                setSnackbarMessage(language === 'en' ? 'Request sent successfully!' : 'Đã gửi yêu cầu thành công!');
                setOpenSnackbar(true);
                // Sign out so they can try again later
                signOut(auth);
            } catch (error) {
                console.error(error);
                setSnackbarMessage(language === 'en' ? 'Failed to send request' : 'Gửi yêu cầu thất bại');
                setOpenSnackbar(true);
            }
        }
    };

    const handleCancelRequest = () => {
        signOut(auth);
        setShowRequestForm(false);
        setPendingUser(null);
    };

    return (
        <div style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden'
        }}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { sm: '20px' }
                }}
            >
                <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
                    <LanguageSelect value={language} onChange={(value) => setLanguage(value)} />
                </Box>

                <Box
                    sx={{
                        width: { xs: '90%', sm: '420px' },
                        padding: { xs: '32px', sm: '40px' },
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(24px)',
                        WebkitBackdropFilter: 'blur(24px)',
                        border: '1px solid rgba(74, 107, 44, 0.15)',
                        boxShadow: '0 12px 40px rgba(74, 107, 44, 0.15)',
                        maxWidth: '420px',
                        boxSizing: 'border-box',
                        transition: 'transform 0.4s ease, box-shadow 0.4s ease',
                        '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: '0 16px 50px rgba(74, 107, 44, 0.2)',
                        }
                    }}
                >
                    {!showRequestForm ? (
                        <Stack direction={'column'} alignItems={'center'} spacing={3}>
                            <img src="/image/logo.png" alt="Clan Logo" style={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', boxShadow: '0 4px 20px rgba(74, 107, 44, 0.2)' }} onError={(e) => { e.currentTarget.src = '/image/login_bg.jpeg' }} />
                            <Typography sx={{ 
                                fontFamily: 'Inter', 
                                fontWeight: 800, 
                                fontSize: { xs: '28px', sm: '34px' }, 
                                background: 'linear-gradient(45deg, var(--text-primary), var(--accent-color))',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                textAlign: 'center', 
                                letterSpacing: '-0.8px' 
                            }}>
                                {language === 'en' ? 'Clan Family Tree' : 'Gia Phả Dòng Họ'}
                            </Typography>
                            
                            <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '15px', color: 'var(--text-secondary)', textAlign: 'center', mb: 1 }}>
                                {language === 'en' ? 'Sign in to access your family tree' : 'Đăng nhập để truy cập gia phả của bạn'}
                            </Typography>

                            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', py: 1 }}>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(74, 107, 44, 0.15)' }} />
                                <Typography sx={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    {language === 'en' ? 'Welcome' : 'Xin chào'}
                                </Typography>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(74, 107, 44, 0.15)' }} />
                            </Stack>

                            <Button 
                                variant="outlined"
                                onClick={handleLogin}
                                startIcon={<GoogleIcon />}
                                sx={{ 
                                    width: '100%', 
                                    padding: '14px 16px',
                                    fontSize: '16px',
                                    borderRadius: '10px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    color: 'var(--text-primary)',
                                    border: '1.5px solid rgba(74, 107, 44, 0.4)',
                                    background: 'rgba(255, 255, 255, 0.4)',
                                    backdropFilter: 'blur(8px)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        background: 'rgba(74, 107, 44, 0.08)',
                                        border: '1.5px solid var(--accent-color)',
                                        transform: 'scale(1.02)'
                                    }
                                }}
                            >
                                {language === 'en' ? 'Continue with Google' : 'Tiếp tục với Google'}
                            </Button>

                            <Typography sx={{ fontSize: '12px', color: 'var(--text-secondary)', textAlign: 'center', mt: 2, opacity: 0.8 }}>
                                {language === 'en' ? 'By continuing, you agree to our Terms of Service and Privacy Policy.' : 'Bằng việc tiếp tục, bạn đồng ý với Điều khoản và Chính sách bảo mật.'}
                            </Typography>
                        </Stack>
                    ) : (
                        <Stack direction={'column'} alignItems={'center'} spacing={2}>
                            <img src="/image/logo.png" alt="Clan Logo" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }} onError={(e) => { e.currentTarget.src = '/image/login_bg.jpeg' }} />
                            <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: { xs: '20px', sm: '22px' }, color: 'var(--text-primary)', textAlign: 'center' }}>
                                {language === 'en' ? 'Access Denied' : 'Tài khoản chưa có quyền'}
                            </Typography>
                            
                            {!requestSent ? (
                                <>
                                    <Typography sx={{ fontFamily: 'Inter', fontSize: '15px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                                        {language === 'en' ? `Your email (${pendingUser?.email}) is not in the system. Would you like to request access?` : `Email của bạn (${pendingUser?.email}) chưa có trong hệ thống. Bạn có muốn gửi yêu cầu tham gia không?`}
                                    </Typography>
                                    <Button variant="contained" fullWidth onClick={handleRequestAccess} sx={{ mt: 2, borderRadius: '12px', py: 1.5, fontWeight: 600, bgcolor: 'var(--accent-color)', '&:hover': {bgcolor: 'var(--accent-hover)'} }}>
                                        {language === 'en' ? 'Send Request' : 'Gửi yêu cầu tham gia'}
                                    </Button>
                                    <Button variant="text" fullWidth onClick={handleCancelRequest} sx={{ color: 'var(--text-secondary)', mt: 1, '&:hover': {color: 'var(--text-primary)'} }}>
                                        {language === 'en' ? 'Cancel' : 'Hủy'}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Typography sx={{ fontFamily: 'Inter', fontSize: '15px', color: 'var(--text-secondary)', textAlign: 'center' }}>
                                        {language === 'en' ? 'Your request has been sent to the admin. Please wait for approval.' : 'Yêu cầu của bạn đã được gửi tới Quản trị viên. Vui lòng chờ phê duyệt.'}
                                    </Typography>
                                    <Button variant="contained" fullWidth onClick={handleCancelRequest} sx={{ mt: 2, borderRadius: '12px', py: 1.5, bgcolor: 'rgba(74, 107, 44, 0.1)', color: 'var(--accent-color)', '&:hover': { bgcolor: 'rgba(74, 107, 44, 0.2)' }, fontWeight: 600 }}>
                                        {language === 'en' ? 'Back to Login' : 'Quay lại đăng nhập'}
                                    </Button>
                                </>
                            )}
                        </Stack>
                    )}
                </Box>
                <Typography sx={{ 
                    position: 'absolute', 
                    bottom: '12px', 
                    fontSize: { xs: '12px', sm: '14px' },
                    color: 'var(--text-secondary)'
                }}>
                    version: {version}
                </Typography>
            </Box>
            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={() => setOpenSnackbar(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="info" variant="filled" onClose={() => setOpenSnackbar(false)}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}